import { AUTH_URL } from './config.js';
import { openAuthPopup } from './oauth.js';
import {
  isRecord,
  ok,
  jsonInit,
  requestJson,
  requestVoid,
} from './accountHttp.js';
import type { AccountResult } from './accountHttp.js';
export type { AccountError, AccountErrorKind, AccountResult } from './accountHttp.js';

/**
 * Typed client for the account-management routes added in M2 (T6–T10):
 * linking/unlinking identities, listing and revoking signed-in devices, and
 * account deletion. See `accountHttp.ts` for the shared request plumbing and
 * the `AccountResult<T>` never-throws shape every function here returns.
 */

export interface Identity {
  provider: string;
  linkedAt: number;
}

export interface DeviceSession {
  publicId: string;
  label: string;
  createdAt: number;
  lastUsedAt: number | null;
  current: boolean;
}

/** Providers `/auth/<provider>/start` accepts today. */
export type LinkableProvider = 'google' | 'github' | 'discord';

function toIdentity(raw: unknown): Identity | null {
  if (!isRecord(raw)) return null;
  const provider = raw['provider'];
  const linkedAt = raw['linkedAt'];
  if (typeof provider !== 'string' || typeof linkedAt !== 'number') return null;
  return { provider, linkedAt };
}

function toIdentityList(body: unknown): Identity[] | null {
  if (!isRecord(body)) return null;
  const entries = body['identities'];
  if (!Array.isArray(entries)) return null;
  const identities: Identity[] = [];
  for (const entry of entries) {
    const identity = toIdentity(entry);
    if (identity === null) return null;
    identities.push(identity);
  }
  return identities;
}

function toDeviceSession(raw: unknown): DeviceSession | null {
  if (!isRecord(raw)) return null;
  const publicId = raw['publicId'];
  const label = raw['label'];
  const createdAt = raw['createdAt'];
  const lastUsedAt = raw['lastUsedAt'];
  const current = raw['current'];
  if (typeof publicId !== 'string' || typeof label !== 'string') return null;
  if (typeof createdAt !== 'number') return null;
  if (lastUsedAt !== null && typeof lastUsedAt !== 'number') return null;
  if (typeof current !== 'boolean') return null;
  return { publicId, label, createdAt, lastUsedAt, current };
}

function toDeviceSessionList(body: unknown): DeviceSession[] | null {
  if (!isRecord(body)) return null;
  const entries = body['sessions'];
  if (!Array.isArray(entries)) return null;
  const sessions: DeviceSession[] = [];
  for (const entry of entries) {
    const session = toDeviceSession(entry);
    if (session === null) return null;
    sessions.push(session);
  }
  return sessions;
}

function toLinkId(body: unknown): string | null {
  if (!isRecord(body)) return null;
  const linkId = body['linkId'];
  return typeof linkId === 'string' ? linkId : null;
}

/** `GET /auth/identities` — every provider linked to the caller's account. */
export function listIdentities(): Promise<AccountResult<Identity[]>> {
  return requestJson('/auth/identities', { method: 'GET' }, toIdentityList);
}

/**
 * `POST /auth/link/start` followed by building the same
 * `…/auth/<provider>/start?origin=<origin>&link=<id>` URL shape `openSignIn`
 * builds without the trailing `link` param — see the plan's "How the link
 * request travels". Returns the URL rather than opening it, so a caller that
 * wants to open it itself doesn't have to duplicate the request;
 * `openLinkPopup` below is the common case.
 */
export async function startLink(
  provider: LinkableProvider,
): Promise<AccountResult<string>> {
  const linkId = await requestJson('/auth/link/start', { method: 'POST' }, toLinkId);
  if (!linkId.ok) {
    return linkId;
  }
  const url =
    AUTH_URL + '/auth/' + provider + '/start?origin=' + location.origin + '&link=' + linkId.value;
  return ok(url);
}

/**
 * Mints a link id, then reuses `oauth.ts`'s popup/PWA-redirect mechanics to
 * open the provider's start URL. Resolves with the linked provider's name on
 * success, `null` on failure or cancellation — the same nullable shape
 * `openSignIn` uses, since this is a sibling popup flow rather than one of
 * the plain REST calls in this module.
 */
export async function openLinkPopup(provider: LinkableProvider): Promise<string | null> {
  const started = await startLink(provider);
  if (!started.ok) {
    return null;
  }

  return openAuthPopup<string>(started.value, (data, resolve) => {
    if (data['success'] !== true) {
      return;
    }
    const linked = data['linked'];
    resolve(typeof linked === 'string' ? linked : provider);
  });
}

/**
 * `DELETE /auth/identities` — unlinks one provider. `409 conflict` means the
 * caller tried to remove their last identity; the server refuses because it
 * is the only credential left to sign back in with.
 */
export function unlinkIdentity(provider: string): Promise<AccountResult<void>> {
  return requestVoid('/auth/identities', jsonInit('DELETE', { provider }));
}

/** `GET /auth/sessions` — every live session on the caller's account. */
export function listSessions(): Promise<AccountResult<DeviceSession[]>> {
  return requestJson('/auth/sessions', { method: 'GET' }, toDeviceSessionList);
}

/** `POST /auth/sessions/revoke` — ends one device's session by its `publicId`. */
export function revokeSession(publicId: string): Promise<AccountResult<void>> {
  return requestVoid('/auth/sessions/revoke', jsonInit('POST', { publicId }));
}

/** `POST /auth/sessions/revoke-all` — ends every session except this one. */
export function revokeAllSessions(): Promise<AccountResult<void>> {
  return requestVoid('/auth/sessions/revoke-all', { method: 'POST' });
}

/**
 * `DELETE /auth/account` — a hard cascade. `confirm` must match the caller's
 * handle (or display name, when they have none) exactly, per the server's
 * typed-confirmation guard.
 */
export function deleteAccount(confirm: string): Promise<AccountResult<void>> {
  return requestVoid('/auth/account', jsonInit('DELETE', { confirm }));
}
